export type VoiceMode = 'full' | 'basic' | 'silent';

const BASIC_PHRASES: Record<string, Record<string, string>> = {
  en: {
    success: 'Transaction processed. Please review.',
    error: 'Task failed. Please check.',
    confirm: 'Please confirm.',
    cancelled: 'Cancelled.',
    name: 'Name?',
    amount: 'Amount?',
    where: 'Where?',
    done: 'Done.',
  },
  ar: {
    success: 'تمت المعالجة. يرجى المراجعة.',
    error: 'فشلت المهمة. يرجى التحقق.',
    confirm: 'يرجى التأكيد.',
    cancelled: 'تم الإلغاء.',
    name: 'الاسم؟',
    amount: 'المبلغ؟',
    where: 'أين؟',
    done: 'تم.',
  },
  hi: {
    success: 'लेन-देन संसाधित हो गया, कृपया समीक्षा करें',
    error: 'कार्य विफल। कृपया जांचें।',
    confirm: 'कृपया पुष्टि करें।',
    cancelled: 'रद्द।',
    name: 'नाम?',
    amount: 'राशि?',
    where: 'कहाँ?',
    done: 'हो गया।',
  },
  ur: {
    success: 'لین دین مکمل۔ براہ کرم جائزہ لیں۔',
    error: 'کام ناکام۔ براہ کرم چیک کریں۔',
    confirm: 'براہ کرم تصدیق کریں۔',
    cancelled: 'منسوخ۔',
    name: 'نام؟',
    amount: 'رقم؟',
    where: 'کہاں؟',
    done: 'ہو گیا۔',
  },
  bn: {
    success: 'লেনদেন সম্পন্ন। অনুগ্রহ করে পর্যালোচনা করুন।',
    error: 'কাজ ব্যর্থ। অনুগ্রহ করে পরীক্ষা করুন।',
    confirm: 'অনুগ্রহ করে নিশ্চিত করুন।',
    cancelled: 'বাতিল।',
    name: 'নাম?',
    amount: 'পরিমাণ?',
    where: 'কোথায়?',
    done: 'সম্পন্ন।',
  },
  fr: {
    success: 'Transaction traitée. Veuillez vérifier.',
    error: 'Échec. Veuillez vérifier.',
    confirm: 'Veuillez confirmer.',
    cancelled: 'Annulé.',
    name: 'Nom ?',
    amount: 'Montant ?',
    where: 'Où ?',
    done: 'Terminé.',
  },
  de: {
    success: 'Transaktion verarbeitet. Bitte prüfen.',
    error: 'Aufgabe fehlgeschlagen. Bitte prüfen.',
    confirm: 'Bitte bestätigen.',
    cancelled: 'Abgebrochen.',
    name: 'Name?',
    amount: 'Betrag?',
    where: 'Wo?',
    done: 'Fertig.',
  },
  zh: {
    success: '交易已处理，请查看。',
    error: '任务失败，请检查。',
    confirm: '请确认。',
    cancelled: '已取消。',
    name: '名称？',
    amount: '金额？',
    where: '哪里？',
    done: '完成。',
  },
  ja: {
    success: '処理が完了しました。ご確認ください。',
    error: '失敗しました。ご確認ください。',
    confirm: '確認してください。',
    cancelled: 'キャンセルしました。',
    name: '名前は？',
    amount: '金額は？',
    where: 'どこ？',
    done: '完了。',
  },
};

const SUPPORTED_LANGUAGES = ['en', 'ar', 'hi', 'ur', 'bn', 'fr', 'de', 'zh', 'ja'];

function normalizeLanguage(language?: string) {
  const code = (language || 'en').toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.includes(code) ? code : 'en';
}

function phrase(key: string, language?: string) {
  const lang = normalizeLanguage(language);
  return BASIC_PHRASES[lang]?.[key] || BASIC_PHRASES.en[key];
}

function isErrorLike(message: string) {
  const text = (message || '').trim();
  if (/\?/.test(text) && /\b(would you like|should i|do you mean|do you want|shall i)\b/i.test(text)) {
    return false;
  }
  return /\b(not found|failed|failure|error|could not|unable to|invalid|sorry)\b/i.test(text);
}

function isActionableError(message: string) {
  const text = (message || '').trim();
  return /\b(no longer exists|assign a (?:valid )?customer|update the quote|already converted|cannot be converted)\b/i.test(text);
}

function isCancelled(message: string) {
  return /\b(cancelled|canceled|never mind|forget it)\b/i.test(message);
}

function isUnifiedQuoteConfirmation(message: string) {
  const text = (message || '').trim();
  if (/ — confirm\?\s*$/i.test(text)) return true;
  if (/,\s*confirm\?\s*$/i.test(text)) return true;
  if (/[A-Z]{3}\s+[\d,.]+\s+for\s+.+\?\s*$/i.test(text)) return true;
  if (/\bcreate a quote for\b/i.test(text) && /\bsay yes to confirm\b/i.test(text)) return true;
  return false;
}

function isPreservedActionConfirmation(message: string) {
  const text = (message || '').trim();
  if (isUnifiedQuoteConfirmation(text)) return true;
  if (/\bsay yes to confirm\b/i.test(text)) return true;
  if (/\bcreate invoice for\b/i.test(text) && /\?/.test(text)) return true;
  return false;
}

function isClarificationQuestion(message: string) {
  const text = (message || '').trim();
  if (!/\?/.test(text)) return false;
  if (/\b(is this correct|say yes to confirm|should i create this|should i proceed)\b/i.test(text)) {
    return false;
  }
  return /\b(what|who|which|how much|how many|where|when|please provide|tell me|say the|pick or say|would you like to|can help you create)\b/i.test(text);
}

function isConfirmation(message: string) {
  const text = (message || '').trim();
  if (isPreservedActionConfirmation(text)) return false;
  if (isClarificationQuestion(text)) return false;
  return /\?/.test(text) && /\b(should i|shall i|is this correct|do you want me to|would you like me to (?:create|proceed)|confirm this)\b/i.test(text);
}

function extractNavigationLabel(message: string) {
  const match = message.match(/(?:to|opening)\s+([^.!?\n]+)/i);
  return match?.[1]?.trim();
}

function isQuoteSelectionPrompt(message: string) {
  const text = (message || '').trim();
  return /\bwhich quote\b/i.test(text) || /\bpick or say a number\b/i.test(text);
}

function isGatheringLike(message: string, phase?: string, action?: string) {
  if (
    phase === 'gathering_data'
    || phase === 'gathering_items'
    || phase === 'gathering_customer'
    || phase === 'gathering_quote'
    || phase === 'clarification'
  ) {
    return true;
  }
  if (action === 'clarify') return true;
  const text = (message || '').trim();
  return /\b(first item or service|what's the first|how many|price per|add another item|who is this quote for|which quote|pick or say a number)\b/i.test(text);
}

function shortenQuestion(message: string, language?: string) {
  const text = message.trim();

  if (isPreservedActionConfirmation(text)) return text;

  if (isConfirmation(text)) return phrase('confirm', language);
  if (isQuoteSelectionPrompt(text)) {
    const firstSentence = text.match(/^[^.!?\n]+[.?]/);
    return firstSentence?.[0]?.trim() || 'Which quote?';
  }
  if (/\b(customer|client)\b/i.test(text) && /\b(invoice|quote|bill)\b/i.test(text)) return 'Customer?';
  if (/\b(supplier|vendor)\b/i.test(text) && /\b(invoice|bill)\b/i.test(text)) return 'Supplier?';
  if (/\b(name|customer|contact|supplier|vendor|who)\b/i.test(text)) return phrase('name', language);
  if (/\b(amount|how much|price|total)\b/i.test(text)) return phrase('amount', language);
  if (/\bwhere\b/i.test(text)) return phrase('where', language);

  if (/what('s| is) the (.+)\?/i.test(text)) {
    const entity = text.match(/what('s| is) the (.+)\?/i)?.[2];
    if (entity) return `${entity.charAt(0).toUpperCase()}${entity.slice(1)}?`;
  }

  const firstSentence = text.match(/^[^.!?\n]+[.?]/);
  if (firstSentence && firstSentence[0].length <= 48) return firstSentence[0].trim();
  if (/\bfirst item or service\b/i.test(text)) return 'First item?';
  if (/\bhow many\b/i.test(text)) return 'How many?';
  if (/\bprice\b/i.test(text)) return phrase('amount', language);
  if (isClarificationQuestion(text)) return firstSentence?.[0]?.trim() || text.slice(0, 80);

  return text;
}

export function shortenToBasic(message: string, language?: string, options: { phase?: string; action?: string } = {}): string {
  const text = (message || '').trim();
  if (!text) return text;

  const gathering = isGatheringLike(text, options.phase, options.action);

  if (isPreservedActionConfirmation(text)) return text;
  if (isUnifiedQuoteConfirmation(text)) return text;
  if (isQuoteSelectionPrompt(text)) {
    const firstSentence = text.match(/^[^.!?\n]+[.?]/);
    return firstSentence?.[0]?.trim() || 'Which quote?';
  }

  if (isErrorLike(text) && !isActionableError(text)) return phrase('error', language);
  if (isCancelled(text)) return phrase('cancelled', language);

  if (/\b(navigat|taking you|opening)\b/i.test(text)) {
    const destination = extractNavigationLabel(text);
    if (destination) return destination;
  }

  if (!gathering && /\b(success|completed|created|recorded|processed|has been|done)\b/i.test(text.toLowerCase())) {
    if (text.length <= 40 && !/\b(I can|you can|for example|would you like)\b/i.test(text)) {
      return text;
    }
    return phrase('success', language);
  }

  if (text.includes('?')) return shortenQuestion(text, language);

  if (!gathering && text.length > 80) return phrase('success', language);

  return text;
}

export function getSpokenMessage(text: string, voiceMode: VoiceMode | undefined, language?: string): string {
  const message = (text || '').trim();
  if (!message || voiceMode === 'silent') return '';

  if (voiceMode === 'basic') {
    return shortenToBasic(message, language);
  }

  return message;
}

export function getDashboardProactiveGreeting(language?: string): string {
  switch (normalizeLanguage(language)) {
    case 'ar':
      return 'مرحباً! أنا مساعدك على لوحة المعلومات. يمكنك طرح سؤال أو اختيار أحد الاقتراحات أدناه.';
    case 'hi':
      return 'आपके डैशबोर्ड में स्वागत है! मैं वित्तीय सारांश, खर्च विश्लेषण और रिपोर्ट में मदद कर सकता/सकती हूँ। नीचे एक सुझाव आज़माएँ।';
    case 'ur':
      return 'آپ کے ڈیش بورڈ میں خوش آمدید! میں مالی خلاصہ، اخراجات کا تجزیہ اور رپورٹس میں مدد کر سکتا/سکتی ہوں۔ نیچے ایک تجویز آزمائیں۔';
    case 'bn':
      return 'আপনার ড্যাশবোর্ডে স্বাগতম! আমি আর্থিক সারাংশ, খরচ বিশ্লেষণ এবং রিপোর্টে সাহায্য করতে পারি। নিচে একটি পরামর্শ চেষ্টা করুন।';
    case 'fr':
      return 'Bienvenue sur votre tableau de bord ! Je peux résumer vos finances, analyser les dépenses ou générer des rapports. Essayez une suggestion ci-dessous.';
    case 'de':
      return 'Willkommen auf Ihrem Dashboard! Ich kann Finanzdaten zusammenfassen, Ausgaben analysieren oder Berichte erstellen. Probieren Sie einen Vorschlag unten.';
    case 'zh':
      return '欢迎使用仪表板！我可以总结财务状况、分析支出或生成报告。请尝试下方的建议查询。';
    case 'ja':
      return 'ダッシュボードへようこそ！財務の概要、支出分析、レポート作成をお手伝いできます。下の提案をお試しください。';
    default:
      return 'Welcome to your dashboard! I can summarize your finances, analyze expenses, or help with reports. Try a suggested query below.';
  }
}

/** True when a message is only the auto-injected dashboard welcome (not user conversation). */
export function isDashboardProactiveGreeting(message: string): boolean {
  const text = (message || '').trim();
  if (!text) return false;

  return SUPPORTED_LANGUAGES.some(
    (lang) => text === getDashboardProactiveGreeting(lang)
  );
}

export function getLocalizedVoiceTestMessage(
  language: string,
  name: string,
  mode: VoiceMode
): string {
  if (mode === 'silent') return '';

  if (mode === 'basic') {
    return shortenToBasic(phrase('success', language), language);
  }

  switch (normalizeLanguage(language)) {
    case 'ar':
      return `مرحباً! أنا ${name}. يمكنني تقديم شرح مفصل واقتراحات والرد على أسئلتك. كيف يمكنني مساعدتك اليوم؟`;
    case 'hi':
      return `नमस्ते! मैं ${name} हूँ। मैं विस्तृत जानकारी, सुझाव और बातचीत में आपकी मदद कर सकता/सकती हूँ। आज मैं आपकी कैसे सहायता कर सकता/सकती हूँ?`;
    case 'ur':
      return `سلام! میں ${name} ہوں۔ میں تفصیلی وضاحتیں، تجاویز اور فطری گفتگو میں آپ کی مدد کر سکتا/سکتی ہوں۔ آج میں آپ کی کیا مدد کر سکتا/سکتی ہوں؟`;
    case 'bn':
      return `হ্যালো! আমি ${name}। আমি বিস্তারিত ব্যাখ্যা, পরামর্শ এবং কথোপকথনে সাহায্য করতে পারi।`;
    case 'fr':
      return `Bonjour ! Je suis ${name}. Je peux fournir des explications détaillées, des suggestions et des réponses conversationnelles. Comment puis-je vous aider aujourd'hui ?`;
    case 'de':
      return `Hallo! Ich bin ${name}. Ich kann ausführliche Erklärungen, Vorschläge und natürliche Gespräche anbieten. Wobei kann ich Ihnen heute helfen?`;
    case 'zh':
      return `你好！我是${name}。我可以提供详细说明、建议和对话式回复。今天我可以如何帮助您？`;
    case 'ja':
      return `こんにちは！私は${name}です。詳しい説明、提案、会話形式の応答ができます。本日どのようにお手伝いできますか？`;
    default:
      return `Hello! This is ${name} in full conversational mode. I can provide detailed explanations, suggestions, and have natural conversations with you. How can I assist you today?`;
  }
}
